import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { WebsiteConfig } from "../types/core";
import { safeHref } from "../lib/safe-href";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { useWBConsent } from "../consent/WBConsentProvider";
import { resolveText } from "../lib/resolve-text";
import { resolveWbChrome } from "../locale/wbChrome";

// Consent banner. The decision itself lives in WBConsentProvider — this is
// only its UI: shown while the state is `unset` (and again after a withdrawal
// from the legal line), hidden once the visitor chose.
//
// Two buttons, same size, same colours, same row: declining must be exactly as
// easy as accepting. Chrome copy comes from `wbChrome` (57 languages, the
// visitor's content language) — it used to come from the de/en `t()` table
// while the legal line under it already spoke 57.
//
// `cookieEnabled === false` ⇒ no banner. That does NOT open the gates: the
// state stays `unset`, third-party iframes stay placeholders, and each
// placeholder's own button is the consent path (see ConsentGate).

type CookieBannerProps = {
  websiteConfig: WebsiteConfig;
};

export function CookieBanner({ websiteConfig }: CookieBannerProps) {
  const consent = useWBConsent();
  const [visible, setVisible] = useState(false);
  const { currentRounding } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const chrome = resolveWbChrome(currentLang, primaryLanguage);
  const headerText = resolveText(websiteConfig.cookieHeaderText, currentLang, primaryLanguage);
  const bodyText = resolveText(websiteConfig.cookieBodyText, currentLang, primaryLanguage);
  const bannerEnabled = websiteConfig.cookieEnabled !== false;

  useEffect(() => {
    setVisible(bannerEnabled && consent.state === "unset");
  }, [bannerEnabled, consent.state]);

  // "Cookie settings" sits at the very bottom of the page and the banner
  // mounts elsewhere in the DOM: without this a keyboard or screen-reader user
  // gets no signal that anything happened. Only after a withdrawal — stealing
  // focus on first load would be hostile.
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (consent.reopenSignal > 0 && visible) box.current?.focus();
  }, [consent.reopenSignal, visible]);

  const buttonClass = `flex-1 px-4 py-2 ${currentRounding} text-sm font-medium cursor-pointer`;
  const buttonStyle = {
    backgroundColor: websiteConfig.colorCookieButton || websiteConfig.primaryColor || "#222222",
    color: websiteConfig.colorCookieButtonText || websiteConfig.backgroundColor || "#f2f2f2",
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-lg"
          ref={box}
          tabIndex={-1}
          role="dialog"
          aria-labelledby="wb-consent-title"
          data-testid="wb-consent-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <div
            className="rounded-2xl shadow-2xl border border-gray-200 p-6"
            style={{
              backgroundColor:
                websiteConfig.backgroundCookieColor || "#ffffff",
            }}
          >
            <h3
              id="wb-consent-title"
              className="text-lg font-semibold mb-2"
              style={{
                color: websiteConfig.colorCookieHeader || undefined,
                fontFamily: websiteConfig.fontCookieHeader || undefined,
              }}
            >
              {headerText || chrome.cookieDefaultHeader}
            </h3>

            <p
              className="text-sm mb-2 leading-relaxed"
              style={{
                color: websiteConfig.colorCookieBody || undefined,
                fontFamily: websiteConfig.fontCookieBody || undefined,
              }}
            >
              {bodyText || chrome.cookieDefaultBody}
              {websiteConfig.cookiePolicyUrl?.trim() ? (
                <>
                  {" "}
                  <a
                    href={safeHref(websiteConfig.cookiePolicyUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline"
                    style={{
                      color:
                        websiteConfig.colorCookieLink ||
                        websiteConfig.primaryColor ||
                        websiteConfig.colorCookieBody ||
                        undefined,
                    }}
                  >
                    {chrome.cookieLearnMore}
                  </a>
                </>
              ) : null}
            </p>

            {/* ALWAYS, under the operator's text or the default alike: what
                accepting does and who receives data. A notice-era operator
                text ("We use cookies. OK?") above Accept would otherwise
                collect a consent that is not informed. */}
            <p
              className="text-xs mb-4 leading-relaxed"
              data-testid="wb-consent-providers"
              style={{
                color: websiteConfig.colorCookieBody || undefined,
                fontFamily: websiteConfig.fontCookieBody || undefined,
                opacity: 0.85,
              }}
            >
              {chrome.consentProviders}
            </p>

            {/* Same class, same style, same row — only the label and the
                handler differ. */}
            <div className="flex items-stretch gap-3">
              <button
                type="button"
                onClick={consent.reject}
                className={buttonClass}
                style={buttonStyle}
                data-testid="wb-consent-reject"
              >
                {chrome.cookieReject}
              </button>
              <button
                type="button"
                onClick={consent.accept}
                className={buttonClass}
                style={buttonStyle}
                data-testid="wb-consent-accept"
              >
                {chrome.cookieAccept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
